import { useMediaQuery, Drawer, Card, CardHeader, CardContent, Grid, Box, FormHelperText, Button } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect, useRef } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import ReadonlyField from "@/components/input/ReadonlyField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { CreateEncryptionKeyInput, EncryptionKeyResponseDto, UpsertDataAccessPolicyInput } from "@/gen/api/encrypt";
import { useUserTimeFormat } from "@/utils/userTimeFormat";


const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(255).required(),
  })

const EditEncryptionKey: FC<{
  open: boolean;
  setOpen: (x: boolean) => void;
}> = ({ open, setOpen }) => {
  
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const record = useRecordContext<EncryptionKeyResponseDto&RecPatch>()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
  const save = useSimpleSaveAction()
  
  const initialValues = {
    submit: '',
    name: record?.name || '',
  }

  const recordRef = useRef<boolean | null>(!!record)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const payload: CreateEncryptionKeyInput = {
      ...values,
    }
    save(payload, options)
  }, [save, record])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm, setValues } = formikCtrl
  
  const handleClose = useCallback(() => { 
    setOpen(false) 
    recordRef.current = null
    resetForm({
      values: { name: '', submit: '', }
    })
  }, [setOpen, resetForm])

  useEffect(() => {
    if (!recordRef.current && !!record) {
      recordRef.current = true
      setValues({
        name: record.name || '',
      } as any)
    }
  }, [record, setValues])

  const format = useUserTimeFormat()
  
  return (
    
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >

      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={mode === 'create' ? "New Encryption Key" : "Edit Encryption Key"} />
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              {mode === 'edit' &&
                <Grid item xs={12} >
                  <ReadonlyField disabled={true} label="Create Time" value={format(record?.createdTime || 0, 'PPp')} />
                </Grid>
              }
              <Grid item xs={12} >
                <TextField label="Name" name="name"/>
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>

      </OdForm>
    </Drawer>
  )
}

export default EditEncryptionKey;
